import { useRef, useState } from "react";
import { useNotify, type Identifier } from "ra-core";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  SubscriptionStaffCardForm,
  type SubscriptionStaffCardFormHandle,
} from "@/modules/billing/subscriptions/SubscriptionStaffCardForm";

/**
 * Popup to type a client's card by hand. Saves it securely in Stripe on the
 * client's customer (no charge) and hands back the payment method id.
 */
export const AddClientCardDialog = ({
  open,
  onOpenChange,
  companyId,
  contactId,
  clientLabel,
  recipientEmail,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyId?: Identifier | null;
  contactId?: Identifier | null;
  clientLabel?: string;
  recipientEmail: string;
  onSaved: (paymentMethodId: string) => void | Promise<void>;
}) => {
  const notify = useNotify();
  const cardRef = useRef<SubscriptionStaffCardFormHandle>(null);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const paymentMethodId = await cardRef.current?.confirmCard();
      if (!paymentMethodId) throw new Error("Enter the card details first");
      await onSaved(paymentMethodId);
      notify("Card saved", { type: "success" });
      onOpenChange(false);
    } catch (error) {
      notify(error instanceof Error ? error.message : "Could not save card", {
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !saving && onOpenChange(next)}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Add a card</DialogTitle>
          <DialogDescription>
            Saved securely with Stripe for {clientLabel || "this client"}. It is
            not charged until you send.
          </DialogDescription>
        </DialogHeader>
        <SubscriptionStaffCardForm
          ref={cardRef}
          enabled={open}
          billTo={{
            companyId: companyId != null ? Number(companyId) : null,
            contactId: contactId != null ? Number(contactId) : null,
            label: clientLabel ?? "",
          }}
          recipientEmail={recipientEmail}
        />
        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            disabled={saving}
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button type="button" disabled={saving} onClick={() => void save()}>
            {saving ? <Loader2 className="animate-spin" aria-hidden /> : null}
            Save card
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
